import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Wifi, Users, Footprints, Home as HomeIcon, School, Shuffle, MessageCircle } from "lucide-react";
import { Reveal, Kicker, Tag } from "./Reveal";

const STARTING_POINTS = [
  "Individual or family referrals",
  "Private narrative therapy & mentoring",
  "NDIS supports where appropriate",
  "School or education referrals",
  "Kinship & foster carer support",
  "Organisational partnerships",
  "Structured 10–12 week programs",
  "Community & group programs",
];

const PATHWAY = [
  ["Connect", "However you arrive — referral, request or yarn."],
  ["Yarn & Explore", "What matters to you, your hopes, strengths and existing supports."],
  ["Agree on a Starting Point", "A collaboratively developed support outline — never one-size-fits-all."],
  ["Begin Support", "Engagement shaped around your goals, interests and ways of working."],
  ["Reflect & Review", "Regular, strengths-based progress conversations — together."],
  ["Adapt or Transition", "Review goals, adapt supports, or plan transitions, endings or ongoing engagement."],
];

const OUTLINE_ITEMS = [
  "Initial engagement and relationship building",
  "Identifying hopes, strengths and concerns",
  "Agreeing on goals and areas of focus",
  "Identifying preferred activities and ways of working",
  "Regular reflection and progress conversations",
  "Reviewing goals and adapting supports",
  "Planning transitions, endings or ongoing engagement",
];

export const HowSupportBegins = () => (
  <section className="px-6 py-14 sm:py-20 bg-cream" data-testid="how-support-begins">
    <div className="max-w-7xl mx-auto">
      <Reveal className="mb-10">
        <Kicker>How Support Begins & Grows</Kicker>
        <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
          Many starting points. <span className="italic text-terracotta">No one-size-fits-all.</span>
        </h2>
        <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
          People connect with Ngawilyi through different starting points — and you don't need to join a structured
          program to access individual support. Initial yarns identify what matters to you: your preferred goals,
          strengths, existing supports and the type of engagement you're seeking.
        </p>
        <div className="mt-7 flex flex-wrap gap-2.5">
          {STARTING_POINTS.map((s) => (
            <span key={s} className="rounded-full bg-creamdark border border-ochre/35 px-4 py-2 text-[13px] font-semibold text-earth">{s}</span>
          ))}
        </div>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <Reveal className="lg:col-span-7">
          <div className="rounded-[40px] bg-creamdark border border-ochre/25 p-8 sm:p-12">
            {PATHWAY.map(([t, d], i) => (
              <div key={t}>
                <div className="flex items-start gap-5">
                  <span className="shrink-0 h-11 w-11 rounded-full bg-terracotta/12 border border-terracotta/35 flex items-center justify-center font-display italic text-terracotta text-lg">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="py-1.5">
                    <p className="font-display text-2xl text-charcoal">{t}</p>
                    <p className="mt-1 text-sm text-earth/80 leading-relaxed">{d}</p>
                  </div>
                </div>
                {i < PATHWAY.length - 1 && <div className="ml-[22px] h-5 w-px bg-gradient-to-b from-terracotta/40 to-transparent" />}
              </div>
            ))}
          </div>
        </Reveal>
        <div className="lg:col-span-5 space-y-6">
          <Reveal delay={0.1}>
            <div className="rounded-[36px] bg-blush/40 border border-blush p-8">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-terracotta mb-4">Your support outline may include</p>
              <ul className="space-y-3">
                {OUTLINE_ITEMS.map((t) => (
                  <li key={t} className="flex gap-3 items-start text-sm text-earth leading-relaxed">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-terracotta" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="rounded-[36px] bg-creamdark border border-ochre/25 p-8">
              <p className="font-display text-2xl font-light text-charcoal leading-snug">
                Progress is more than attendance.
              </p>
              <p className="mt-3 text-sm text-earth/85 leading-relaxed">
                It can include changes in confidence, relationships, identity, belonging, education engagement,
                emotional wellbeing and connection to community. Progress reviews are collaborative,
                strengths-based and appropriate to your circumstances.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="rounded-[36px] bg-charcoal text-cream p-8 grain relative overflow-hidden">
              <div className="absolute -bottom-14 -right-14 w-44 h-44 bg-terracotta/25 organic-blob blur-2xl" />
              <div className="relative">
                <p className="text-xs uppercase tracking-[0.25em] font-bold text-ochre mb-4">Flexible across supports</p>
                <p className="text-sm text-cream/85 leading-relaxed">
                  Entering through one service or program never restricts you to it. A young person referred for
                  education engagement may also benefit from narrative conversations, family connection, mentoring,
                  cultural identity work or movement — explored collaboratively. Any change to agreed services,
                  information sharing, referrals or other professionals is always discussed first and supported by
                  appropriate agreements, permissions and informed consent. Joining one program never automatically
                  authorises others.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const DELIVERY = [
  { icon: MessageCircle, t: "Face-to-face sessions", d: "Individual sessions in a space that works for you." },
  { icon: Users, t: "Community-based & outreach", d: "Support where community and life already happen." },
  { icon: MapPin, t: "Mobile support", d: "At suitable agreed locations — subject to availability, travel and safety." },
  { icon: School, t: "School-based engagement", d: "Working alongside education settings where agreed." },
  { icon: Wifi, t: "Online sessions", d: "Telehealth yarns when distance or timing calls for it." },
  { icon: Shuffle, t: "Hybrid delivery", d: "Combining online and face-to-face engagement." },
  { icon: HomeIcon, t: "Group yarning & community", d: "Circles, cook-ups and community activities." },
  { icon: Footprints, t: "Outdoor & fitness", d: "Walking, movement and physical activity where appropriate." },
];

export const WhereSupportHappens = () => (
  <section className="px-6 py-14 sm:py-20 bg-creamdark grain relative overflow-hidden" data-testid="where-support-happens">
    <div className="absolute -top-24 -right-24 w-96 h-96 bg-terracotta/10 organic-blob blur-3xl" />
    <div className="relative max-w-7xl mx-auto">
      <Reveal className="mb-10">
        <Kicker>Where Support Happens</Kicker>
        <h2 className="font-display font-light text-4xl sm:text-5xl leading-[1.08] text-charcoal max-w-3xl">
          Support doesn't always happen <span className="italic text-terracotta">in a therapy room.</span>
        </h2>
        <p className="mt-6 text-base lg:text-lg leading-relaxed text-earth max-w-3xl">
          Ngawilyi uses a flexible, mobile delivery model. Location and delivery methods are agreed based on your
          needs, safety, accessibility, privacy, consent and service capacity.
        </p>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {DELIVERY.map((m, i) => (
          <Reveal key={m.t} delay={i * 0.05}>
            <div data-testid={`delivery-${i}`} className="rounded-[32px] bg-cream border border-ochre/25 p-7 h-full transition-transform duration-300 hover:-translate-y-1">
              <m.icon className="h-6 w-6 text-terracotta" strokeWidth={1.5} />
              <p className="mt-4 font-semibold text-charcoal">{m.t}</p>
              <p className="mt-2 text-sm text-earth/80 leading-relaxed">{m.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.25}>
        <p className="mt-8 text-sm text-earth/70 leading-relaxed max-w-3xl">
          Mobile and outreach services are subject to availability, location, travel arrangements and relevant
          safety requirements — always agreed with you first.
        </p>
        <Link data-testid="delivery-request-link" to="/request-support" className="group mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-terracotta">
          Find your starting point <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
    </div>
  </section>
);
